/**
 * F2 — Schedule block types: PLUGGABLE registry.
 *
 * Adaptation of the block engine from `aula` (infra/guides/LEARNING_BLOCKS.md)
 * to the worship domain: the ENGINE (engine.ts) never knows the concrete types
 * — it only orders and sums durations. Each type declares its defaults and
 * adding a new one ("baptism", "holy communion", "drama"…) is
 * `registry.register(def)`: neither the engine nor the database change (that is
 * why `schedule_block.kind` is free text, with no CHECK).
 */

export interface ScheduleKindDef {
  /** Stable identifier (travels in `schedule_block.kind`). */
  id: string;
  /** Default duration when adding a block of this type (minutes). */
  defaultDurationMinutes: number;
}

export class ScheduleKindRegistry {
  private readonly kinds = new Map<string, ScheduleKindDef>();

  register(def: ScheduleKindDef): void {
    if (this.kinds.has(def.id)) {
      throw new Error(`Schedule kind already registered: ${def.id}`);
    }
    this.kinds.set(def.id, def);
  }

  registerAll(defs: readonly ScheduleKindDef[]): void {
    for (const def of defs) {
      this.register(def);
    }
  }

  has(id: string): boolean {
    return this.kinds.has(id);
  }

  get(id: string): ScheduleKindDef | undefined {
    return this.kinds.get(id);
  }

  ids(): string[] {
    return [...this.kinds.keys()];
  }

  /** Default duration of a type (unknown ones fall back to 5 min). */
  defaultDuration(id: string): number {
    return this.kinds.get(id)?.defaultDurationMinutes ?? 5;
  }
}

/** Base worship types — the church can register its own on top. */
export const BASE_SCHEDULE_KINDS: readonly ScheduleKindDef[] = [
  { id: "worship", defaultDurationMinutes: 20 },
  { id: "sermon", defaultDurationMinutes: 35 },
  { id: "prayer", defaultDurationMinutes: 10 },
  { id: "reading", defaultDurationMinutes: 5 },
  { id: "announcement", defaultDurationMinutes: 5 },
  { id: "offering", defaultDurationMinutes: 5 },
  { id: "transition", defaultDurationMinutes: 2 },
  { id: "other", defaultDurationMinutes: 5 },
];

export function defaultScheduleKinds(): ScheduleKindRegistry {
  const registry = new ScheduleKindRegistry();
  registry.registerAll(BASE_SCHEDULE_KINDS);
  return registry;
}
